import { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { useTexture } from "@react-three/drei";
import { AnimatePresence } from "framer-motion";
import * as THREE from "three";
import { ProjectionHall, useHallUniforms } from "./ProjectionHall";
import { HallDirector, hallInfo } from "./HallDirector";
import { arrive, focus as camFocus, glide, glideOverview, glideTo, glideToSlot, lights, Player, portal, pose, SIGN, skipGlide, STAND, standBefore } from "./Player";
import { PortalVeil } from "./PortalVeil";
import { RoomEnd, RoomSteps } from "./RoomSteps";
import { feelingName, RAINBOW_ORDER, SECTIONS } from "../../lib/sections";
import { RoomsMenu } from "./RoomsMenu";
import { Rooms } from "./Rooms";
import { Joystick } from "./Joystick";
import { BenchLabel, FlipbookLabel, ControlsHint, FocusLabel, Loader, Reticle, TopBar } from "./Hud";
import { BenchWall } from "./BenchWall";
import { SeatCamera, cine } from "./SeatCamera";
import { ALCOVE, BOOK, BOOK_SIGN, ROOM } from "../../lib/layout";
import { frontOf, minSpacing, regionAt, roomEntry, worldLayout } from "../../lib/world";
import { imageSrc, isTouchDevice } from "../../lib/api";
import { glHealthy, glStart, immersionMode, mark, noteTap, perf, times } from "../../lib/perf";
import { StepNudge } from "./StepNudge";
import { LivingWindows } from "./LivingWindows";
import { HallDecor } from "./HallDecor";
import { FloorPath } from "./FloorPath";
import { MapButton, MapPanel, markVisited, NextChip, TourBar, useTour, useVisited } from "./Guide";
import { P27_OFF } from "./ProjectionHall";
import { diag, diagNote, diagState, rendererOf } from "../../lib/diag";
const P28_OFF = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("p28") === "off";

const calmMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || immersionMode() === "simple";

const WALK_KEY = "afae-walk";
// an old remembered "walk freely" must never decide how rooms open: walk freely lasts only for the visit that chose it
localStorage.removeItem(WALK_KEY);
const HINT_KEY = "afae-hint-dismissed";
export const hallView = { door: () => null };
// where the visitor stood in a room, so a rebuilt gallery (lost WebGL context) carries on from the same painting
const kept = { region: null, step: null, stepIdx: 0 };

function Hall({ paintings, groups, slots, onReady, lite, still, bench, region, meshesRef, onHover, onOpen, onEnter, onSpecial, failTest }) {
  // checks only: a gallery build told to fail, to walk the light-3D retry and the painted 2D wall
  if (failTest) throw new Error("test: hall failed");
  const textures = useTexture(paintings.map((p) => imageSrc(p.thumb_url || p.image_url)));
  const { gl, camera } = useThree();
  useLayoutEffect(() => {
    textures.forEach((t) => {
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = gl.capabilities.getMaxAnisotropy();
      t.needsUpdate = true;
    });
  }, [textures, gl]);
  const texById = useMemo(() => Object.fromEntries(paintings.map((p, i) => [p.id, Object.assign(textures[i], { userData: { ...textures[i].userData, slug: p.slug } })])), [paintings, textures]);
  const birds = paintings.find((p) => p.slug === "love-must-feel-like-this");
  const sideTex = ["simmering-passion", "the-butterfly-wing"].map((s) => texById[paintings.find((p) => p.slug === s)?.id]);
  const u = useHallUniforms(groups, texById, textures[0], lite, birds && texById[birds.id], sideTex);
  useEffect(() => onReady(), [onReady]);
  useEffect(() => {
    hallView.door = (slug) => {
      const p = paintings.find((q) => q.slug === slug);
      const s = p && slots[p.id];
      if (!s) return null;
      const v = new THREE.Vector3(s.x, 1.62, s.z).project(camera);
      if (v.z > 1 || Math.abs(v.x) > 1.2 || Math.abs(v.y) > 1.2) return null;
      return { x: ((v.x + 1) / 2) * window.innerWidth, y: ((1 - v.y) / 2) * window.innerHeight };
    };
    return () => (hallView.door = () => null);
  }, [paintings, slots, camera]);
  return (
    <>
      <group visible={region === "hall"}>
        <ProjectionHall u={u} still={still} />
        <LivingWindows paintings={paintings} active={bench.enabled && region === "hall"} still={calmMotion()} lite={still} onOpen={onSpecial} />
        {!P28_OFF && <FloorPath active={bench.enabled && region === "hall"} />}
        {!P27_OFF && (
          <Suspense fallback={null}>
            <HallDecor />
          </Suspense>
        )}
      </group>
      <BenchWall paintings={paintings} texById={texById} u={u} {...bench} />
      <Rooms u={u} groups={groups} slots={slots} texById={texById} fallback={textures[0]} region={region} meshesRef={meshesRef} onHover={onHover} onOpen={onOpen} onEnter={onEnter} />
    </>
  );
}

// Keeps the hidden gallery warm (textures uploaded) at a trickle while the entrance plays.
function Ticker() {
  const invalidate = useThree((st) => st.invalidate);
  useEffect(() => {
    const id = setInterval(() => invalidate(), 450);
    return () => clearInterval(id);
  }, [invalidate]);
  return null;
}

let autoFell = false;
// If the 3D gallery is barely drawing (under 5 frames a second for 10 seconds, ignoring glides, portals and the first
// seconds in a new room while its paintings upload), first drop to the light mode once, then move to the 2D gallery.
function useSlowGuard(off, fallRef, liteRef) {
  useEffect(() => {
    if (off || autoFell || perf.forced === "full") return;
    let frames = 0, low = 0, raf;
    const tick = () => {
      frames++;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    const id = setInterval(() => {
      const busy = glide.on || portal.on || performance.now() - (window.__afaeRegionAt || 0) < 6000;
      low = document.hidden || busy ? 0 : frames < 5 ? low + 1 : 0;
      window.__afaeGuard = { frames, low, stepped: !!liteRef.current?.stepped };
      frames = 0;
      if (low < 10) return;
      low = 0;
      if (liteRef.current && !liteRef.current.stepped) {
        liteRef.current.stepped = true;
        return liteRef.current.go();
      }
      autoFell = true;
      // a slow phone keeps the lighter 3D gallery (adaptive resolution already stepped down); it is never sent to the flat view
      window.__afaeGuard.slow = true;
    }, 1000);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(id);
    };
  }, [off, fallRef, liteRef]);
}

export default function GalleryRoom({ paintings, liteStart = false, paused, idle = false, seated, revealing, visited = [], onOpen, onSpecial, onReveal, onRevealed, onSwitch2D, onFallback, onMusic, onRegion, onMemory, onBook }) {
  const moveRef = useRef({ x: 0, y: 0 });
  const fallRef = useRef();
  fallRef.current = (why) => {
    if (!alive.current) return;
    window.__afaeFallback = why;
    onFallback(why);
  };
  const alive = useRef(true);
  const liteStep = useRef({ stepped: false, go: () => {} });
  window.__afaeLite = () => liteStep.current.go();
  useEffect(() => {
    alive.current = true;
    return () => void (alive.current = false);
  }, []);
  useSlowGuard(paused || idle, fallRef, liteStep);
  useEffect(() => {
    if (!paused && times.nextTap && !times.unpaused) mark("unpaused");
  }, [paused]);
  useEffect(() => {
    glStart();
    window.__afaeGalleryMounts = (window.__afaeGalleryMounts || 0) + 1;
  }, []);
  // a gallery that has drawn for a moment is healthy; only a crash right at start counts toward the flat fallback
  useEffect(() => {
    const id = setTimeout(glHealthy, 2500);
    return () => clearTimeout(id);
  }, []);
  const [benchHot, setBenchHot] = useState(false);
  const [bookHot, setBookHot] = useState(false);
  const walking = !paused && !seated && !revealing;
  const bench = useMemo(() => ({ on: !!(seated || revealing), enabled: walking, onReveal, onRevealed, onHot: setBenchHot, onBookHot: setBookHot }), [seated, revealing, walking, onReveal, onRevealed]);
  const meshesRef = useRef({});
  const [hovered, setHovered] = useState(null);
  const [aimed, setAimed] = useState(null);
  const [near, setNear] = useState(null);
  const [lite, setLite] = useState(liteStart);
  const [failTest] = useState(() => window.__afaeFailHall > 0 && window.__afaeFailHall-- > 0);
  liteStep.current.go = () => {
    setLite(true);
    window.__afaeGL?.gl.setPixelRatio(1);
  };
  const [region, setRegion] = useState(() => regionAt(pose.x, pose.z));
  const resume = useState(() => region !== "hall" && kept.region === region)[0];
  const [ready, setReady] = useState(false);
  const [hint, setHint] = useState(() => !sessionStorage.getItem(HINT_KEY));
  const touch = useMemo(isTouchDevice, []);
  const [guided, setGuided] = useState(() => sessionStorage.getItem(WALK_KEY) !== "free");
  const [stepIdx, setStepIdx] = useState(() => (resume ? kept.stepIdx : 0));
  // warm the next painting's full image so stepping to it (and stepping inside) feels instant
  useEffect(() => {
    const list = groups[region] || [];
    const next = list[stepIdx] || list[0];
    if (walking && next) new Image().src = imageSrc(next.image_url);
  }, [region, stepIdx, walking]); // eslint-disable-line react-hooks/exhaustive-deps
  const [atEnd, setAtEnd] = useState(false);
  const [finale, setFinale] = useState(false);
  const flipWalk = () => {
    sessionStorage.setItem(WALK_KEY, guided ? "free" : "guided");
    setGuided(!guided);
  };

  const { slots, groups } = useMemo(() => worldLayout(paintings), [paintings]);
  // the remembered focus points at this build's slot (a rebuild lays out new slot objects)
  useState(() => {
    const f = camFocus.slot;
    if (f) camFocus.slot = Object.values(slots).find((q) => q.x === f.x && q.z === f.z) || f;
  });
  const byId = useMemo(() => Object.fromEntries(paintings.map((p) => [p.id, p])), [paintings]);
  const focus = byId[hovered] || byId[aimed] || byId[near];

  const enterArch = useCallback((id) => {
    if (!walking || glide.on || portal.on) return;
    glideTo(id);
  }, [walking]);
  const open = useCallback((p) => {
    setHovered(null);
    sessionStorage.setItem(HINT_KEY, "1");
    setHint(false);
    onOpen(p);
  }, [onOpen]);
  const activate = useCallback((id) => byId[id] && open(byId[id]), [byId, open]);
  const step = useRef(resume ? kept.step : null);
  const resumeAt = useRef(resume ? region : null);
  useEffect(() => void Object.assign(kept, { region, step: step.current, stepIdx }), [region, stepIdx]);
  // a portal cut short by the rebuild still lands the visitor where it was taking them
  useEffect(() => {
    if (!portal.on) return;
    portal.on = false;
    if (portal.target) arrive(portal.target);
  }, []);
  const tourRef = useRef(null);
  // a tap or click only ever opens (or draws near, then opens); hearts come from the explicit heart button
  const tap = (p) => {
    tourRef.current?.pauseForTap();
    const s = slots[p.id];
    if (!walking || glide.on || portal.on || !s) return noteTap(`tap-blocked:${!walking ? "paused" : glide.on ? "glide" : portal.on ? "portal" : "noslot"}`);
    const f = frontOf(s, STAND);
    const close = Math.hypot(pose.x - s.x, pose.z - s.z) <= STAND + 0.2 || Math.hypot(pose.x - f.x, pose.z - f.z) < 1 || (camFocus.slot === s && Math.hypot(pose.x - s.x, pose.z - s.z) < 4.6);
    step.current = p.id;
    setStepIdx((groups[s.section] || []).findIndex((x) => x.id === p.id) + 1);
    noteTap(close && regionAt(pose.x, pose.z) === s.section ? `open:${p.slug}` : `approach:${p.slug}`);
    if (close && regionAt(pose.x, pose.z) === s.section) open(p);
    else glideToSlot(s);
  };
  const stepTo = useCallback((dir) => {
    const list = groups[region] || [];
    if (!walking || glide.on || portal.on || !list.length) return;
    const at = list.findIndex((p) => p.id === step.current);
    if (guided && at === list.length - 1 && dir > 0) {
      step.current = null;
      lights.all = true;
      setFinale(true);
      return glideOverview(region, () => {
        setFinale(false);
        setAtEnd(true);
      }, 3);
    }
    lights.all = false;
    setAtEnd(false);
    const i = at < 0 ? (dir > 0 ? 0 : list.length - 1) : (at + dir + list.length) % list.length;
    step.current = list[i].id;
    setStepIdx(i + 1);
    glideToSlot(slots[list[i].id]);
  }, [groups, region, slots, walking, guided]);
  const stepRef = useRef(stepTo);
  stepRef.current = stepTo;
  const walkRef = useRef(walking);
  walkRef.current = walking;
  const places = useVisited();

  const tourApi = useTour({ region, atEnd, stepTo, paused });
  tourRef.current = tourApi;
  useEffect(() => {
    window.__afaeBenchOpen = !!seated;
  }, [seated]);
  const [mapOpen, setMapOpen] = useState(false);
  useEffect(() => {
    const on = () => setMapOpen(true);
    window.addEventListener("afae-map", on);
    return () => window.removeEventListener("afae-map", on);
  }, []);
  useEffect(() => {
    // a rebuilt gallery carries on at the painting the visitor was on
    if (resumeAt.current === region) return;
    resumeAt.current = null;
    step.current = null;
    setStepIdx(0);
    setAtEnd(false);
    setFinale(false);
    lights.all = false;
    if (region === "hall") camFocus.slot = null;
    if (!guided || region === "hall") return;
    // however long the way in takes (slow phones, lighter mode), the room always opens on its painting 1
    const id = setInterval(() => {
      if (glide.on || portal.on || !walkRef.current) return;
      if (!step.current) stepRef.current(1);
      if (step.current) clearInterval(id);
    }, 200);
    return () => clearInterval(id);
  }, [region, guided]);
  useEffect(() => {
    window.__afaeFirst = guided ? (id) => slots[groups[id]?.[0]?.id] : null;
    window.__afaeStepSlot = () => (guided && step.current ? slots[step.current] : null);
    const mv = (e) => (window.__afaePointer = { x: e.clientX, y: e.clientY });
    window.addEventListener("pointerdown", mv);
    return () => window.removeEventListener("pointerdown", mv);
  }, [guided, slots, groups]);
  useEffect(() => {
    window.__afaeFocus = focus || null;
    return () => {
      window.__afaeFocus = null;
    };
  }, [focus]);
  const swipe = useRef(null);
  // event timestamps, not handler time, so a busy frame can't swallow a quick swipe
  const onSwipeDown = (e) => {
    if (guided && region !== "hall") swipe.current = { x: e.clientX, y: e.clientY, t: e.timeStamp };
  };
  const onSwipeUp = (e) => {
    const s = swipe.current;
    swipe.current = null;
    if (!s) return;
    const dx = e.clientX - s.x;
    if (Math.abs(dx) > 60 && Math.abs(e.clientY - s.y) < 80 && e.timeStamp - s.t < 700) stepTo(dx < 0 ? 1 : -1);
  };
  const [nudge, setNudge] = useState(null);
  const candidate = walking && region !== "hall" ? (guided ? (stepIdx > 0 && !atEnd && !finale ? step.current : null) : focus?.id || null) : null;
  useEffect(() => {
    setNudge(null);
    if (!candidate) return;
    let t = null;
    const id = setInterval(() => {
      if (glide.on || portal.on) {
        clearTimeout(t);
        t = null;
      } else if (!t) t = setTimeout(() => setNudge(candidate), 3000);
    }, 250);
    return () => {
      clearInterval(id);
      clearTimeout(t);
    };
  }, [candidate]);
  const nudgeMesh = useCallback(() => meshesRef.current[nudge], [nudge]);
  useEffect(() => {
    window.__afaeNudge = nudge && nudge === candidate ? { slug: byId[nudge]?.slug, subtle: visited.length > 0 } : null;
  }, [nudge, candidate, byId, visited.length]);
  useEffect(() => void (walking && onBook?.(bookHot)), [walking, bookHot, onBook]);
  const memRef = useRef(false);
  // at the Memory Wall the bench's invitation is the one bottom cue: the Feeling Wall prompt, map and turn-around step aside
  const [atMem, setAtMem] = useState(false);
  useEffect(() => {
    if (!walking) return;
    const id = setInterval(() => {
      const near = Math.abs(pose.x) < ALCOVE.w / 2 + 1;
      const at = region === "hall" && near && (pose.z > 5.5 || (pose.z > 3.4 && Math.cos(pose.yaw) < -0.5));
      if (at !== memRef.current) {
        onMemory?.((memRef.current = at));
        setAtMem(at);
      }
    }, 400);
    return () => clearInterval(id);
  }, [walking, region, onMemory]);
  const nextRoom = RAINBOW_ORDER[(RAINBOW_ORDER.indexOf(region) + 1) % RAINBOW_ORDER.length];
  // the visit's last feeling room (last in rainbow order, or all five now seen) ends by offering the Memory Wall
  const lastRoom = RAINBOW_ORDER.includes(region) && (region === RAINBOW_ORDER[RAINBOW_ORDER.length - 1] || RAINBOW_ORDER.every((id) => id === region || places.has(id)));
  const markReady = useCallback(() => setReady(true), []);
  useEffect(() => {
    if (!paused) setHovered(null);
  }, [paused]);
  const dismissHint = () => {
    sessionStorage.setItem(HINT_KEY, "1");
    setHint(false);
  };

  useEffect(() => {
    window.__afae = {
      pose: () => ({ ...pose }),
      setPose: (x, z, yaw, pitch = -0.03) => Object.assign(pose, { x, z, yaw, pitch }),
      tint: () => ({ ...hallInfo }),
      hall: () => ({ ...hallInfo, doors: Object.keys(meshesRef.current).length }),
      fps: () => Math.round(perf.fps),
      aimAt: (slug) => standBefore(paintings, slug),
      toRoom: (id) => Object.assign(pose, roomEntry(id), { pitch: -0.02 }),
      glide: (target) => glideTo(target),
      gliding: () => glide.on || portal.on,
      slotOf: (slug) => ({ ...slots[paintings.find((p) => p.slug === slug)?.id] }),
      rooms: () => Object.fromEntries(Object.entries(groups).map(([k, l]) => [k, l.map((p) => p.slug)])),
      // details → "Next painting ›": the room glides on to that painting, one at a time
      stepToSlug: (slug) => {
        const p = paintings.find((q) => q.slug === slug);
        const s = p && slots[p.id];
        if (!s) return;
        lights.all = false;
        setAtEnd(false);
        step.current = p.id;
        setStepIdx((groups[s.section] || []).findIndex((x) => x.id === p.id) + 1);
        glideToSlot(s);
      },
      region: () => regionAt(pose.x, pose.z),
      spacing: () => +minSpacing(slots).toFixed(2),
      live: () => ({ ...window.__afaeLive }),
      toBench: () => {
        const x = 0.9;
        const z = 4.4;
        return Object.assign(pose, { x, z, yaw: Math.atan2(-(SIGN.x - x), -(SIGN.z - z)), pitch: -0.05 });
      },
      toBook: () => {
        const x = BOOK.x + 0.5;
        const z = BOOK.z - 1.7;
        return Object.assign(pose, { x, z, yaw: Math.atan2(-(BOOK.x - x), -(BOOK.z - z)), pitch: -0.32 });
      },
      book: () => ({ ...BOOK }),
      bookSign: () => ({ ...BOOK_SIGN }),
      place: (p) => Object.assign(pose, p),
      sign: () => ({ ...SIGN, reveal: window.__afaeReveal || null }),
      cine: () => ({ mode: cine.mode, t: +cine.t.toFixed(2), fps: cine.fps, dpr: cine.dpr, cam: { ...cine.cam } }),
    };
    return () => delete window.__afae;
  }, [paintings, slots, groups]);

  return (
    <div data-testid="gallery-room" data-region={region} data-walk={guided ? "guided" : "free"} onPointerDownCapture={onSwipeDown} onPointerUpCapture={onSwipeUp} className={`fixed inset-0 bg-[#EBDDF7] ${hovered ? "cursor-pointer" : "cursor-grab active:cursor-grabbing"}`}>
      <Canvas
        data-testid="gallery-canvas"
        frameloop={paused ? "never" : idle ? "demand" : "always"}
        dpr={liteStart ? 1 : [1, 1.5]}
        camera={{ fov: 66, near: 0.05, far: 60, position: [pose.x, ROOM.EYE, pose.z] }}
        gl={{ antialias: !liteStart, powerPreference: liteStart ? "default" : "high-performance" }}
        onCreated={({ gl, scene, camera }) => {
          gl.toneMapping = THREE.NeutralToneMapping;
          gl.toneMappingExposure = 1.0;
          window.__afaeGL = { gl, scene, camera };
          diagNote({ renderer: rendererOf(gl), view: liteStart ? "3d-lite" : "3d" });
          let lostT;
          gl.domElement.addEventListener("webglcontextlost", (e) => {
            e.preventDefault();
            if (!alive.current) return;
            window.__afaeGLLost = (window.__afaeGLLost || 0) + 1;
            diagNote({ lost: diagState.lost + 1 });
            diag("context-lost", `count ${window.__afaeGLLost}`);
            lostT = setTimeout(() => fallRef.current("context-lost"), 1500);
          });
          gl.domElement.addEventListener("webglcontextrestored", () => clearTimeout(lostT));
        }}
        style={{ touchAction: "none" }}
      >
        {idle && <Ticker />}
        <color attach="background" args={["#EBDDF7"]} />
        <Suspense fallback={null}>
          {paintings.length > 0 && (
            <Hall failTest={failTest} paintings={paintings} groups={groups} slots={slots} onReady={markReady} lite={lite || !!seated} still={lite || calmMotion()} bench={bench} region={region} meshesRef={meshesRef} onHover={setHovered} onOpen={tap} onEnter={enterArch} onSpecial={onSpecial} />
          )}
        </Suspense>
        <Player enabled={walking} moveRef={moveRef} meshesRef={meshesRef} onAim={setAimed} onActivate={activate} />
        <SeatCamera seated={!!seated} revealing={!!revealing} />
        {walking && paintings.length > 0 && (
          <HallDirector paintings={paintings} slots={slots} onNear={setNear} onWalkIn={open} onMusic={onMusic} onLite={() => setLite(true)} onRegion={(r) => {
            window.__afaeRegionAt = performance.now();
            markVisited(r);
            setRegion(r);
            onRegion?.(r);
          }} />
        )}
      </Canvas>
      <TopBar onSwitch2D={onSwitch2D} />
      <PortalVeil />
      {walking && <RoomsMenu region={region} onPick={glideTo} guided={guided} onWalk={flipWalk} onAll={onSwitch2D} />}
      {walking && !P28_OFF && region === "hall" && !atMem && !tourApi.tour.on && <NextChip visited={places} onTour={tourApi.start} />}
      {walking && !P28_OFF && !(atMem && region === "hall") && <MapButton onClick={() => setMapOpen(true)} />}
      <MapPanel open={mapOpen && walking} onClose={() => setMapOpen(false)} region={region} visited={places} />
      {walking && <TourBar api={tourApi} />}
      {walking && region === "hall" && !atMem && (
        <button
          data-testid="turn-around-button"
          onClick={() => glideTo(pose.z > -1 && Math.cos(pose.yaw) < 0 ? "hall" : "windows")}
          className="fixed bottom-24 right-4 z-[40] rounded-full border border-[#E2B66B]/70 bg-[#FFF9EE]/90 px-4 py-1.5 font-display text-lg italic text-[#4A2D55] shadow-[0_10px_30px_-18px_rgba(160,110,40,0.8)] backdrop-blur-md transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-white sm:bottom-20 sm:right-8"
        >
          turn around ↺
        </button>
      )}
      {walking && region !== "hall" && guided && stepIdx > 0 && !atEnd && !finale && (
        <div
          data-testid="focus-vignette"
          className="pointer-events-none fixed inset-0 z-[5] bg-[radial-gradient(ellipse_46%_56%_at_50%_46%,transparent_58%,rgba(36,22,48,0.42)_100%)] backdrop-blur-[2px] [mask-image:radial-gradient(ellipse_46%_56%_at_50%_46%,transparent_60%,black_100%)]"
        />
      )}
      {walking && region !== "hall" && !finale && <RoomSteps onStep={stepTo} guided={guided} index={atEnd ? 0 : stepIdx} total={(groups[region] || []).length} feeling={feelingName(region)} line={SECTIONS[region]?.line} />}
      {walking && region !== "hall" && guided && (
        <RoomEnd show={atEnd} line={SECTIONS[region]?.line} nextName={feelingName(nextRoom)} onBack={() => glideTo("hall")} onNext={() => glideTo(nextRoom)} onMemories={lastRoom ? () => glideTo("memory") : undefined} />
      )}
      {!seated && !revealing && <Reticle active={!!aimed} />}
      {walking && finale && <button data-testid="finale-skip" aria-label="Skip" onClick={skipGlide} className="fixed inset-0 z-[45] cursor-pointer bg-transparent" />}
      {walking && nudge && nudge === candidate && byId[nudge] && <StepNudge mesh={nudgeMesh} painting={byId[nudge]} subtle={visited.length > 0} onOpen={open} />}
      {walking && <FocusLabel painting={atEnd || finale || (nudge && nudge === candidate) ? null : focus} touch={touch} firstHint={false} />}
      <BenchLabel show={walking && benchHot && !atMem && !focus && !tourApi.tour.done} touch={touch} />
      <FlipbookLabel show={walking && bookHot && !focus} touch={touch} />
      {touch && !seated && !guided && <Joystick moveRef={moveRef} />}
      <AnimatePresence>
        {ready && hint && walking && <ControlsHint touch={touch} onDismiss={dismissHint} />}
        {!ready && <Loader />}
      </AnimatePresence>
    </div>
  );
}
